import React from 'react'
import { validatePhoneNumber } from '../helper/common'
import { LocalStorageManager } from '../helper/localStorage'
import { useAppParamsContext } from './AppParamsContext'
import { SDK_RESULT_STATUS, useSdk } from '../sdk'

const APP_USER_CONFIRMED_STORAGE_KEY = 'trafficFineConfirmedUserData'

export const APP_USER_IDENTITY_STATUS = {
  LOADING: 'loading',
  READY: 'ready',
  USING_LOCAL_FALLBACK: 'using_local_fallback',
  CONSENT_FIRST_TIME: 'consent_first_time',
  CONSENT_CHANGED: 'consent_changed',
  MISSING_PHONE: 'missing_phone',
  SDK_AUTHENTICATION_ERROR: 'sdk_authentication_error'
}

const createEmptyUserData = () => ({
  phoneNumberFromSdk: '',
  phoneNumberWithUsernamePrefix: '',
  fullName: '',
  sdkToken: null
})

const cleanPhoneNumber = (phoneNumber) => String(phoneNumber || '').replace(/\D/g, '')

const buildPhoneNumberWithUsernamePrefix = (phoneNumberFromSdk, usernamePrefix) => {
  if (!phoneNumberFromSdk) {
    return ''
  }

  return `${usernamePrefix || ''}${phoneNumberFromSdk}`
}

const formatCurrentUserData = (nextUserData, usernamePrefix) => {
  if (!nextUserData) {
    return createEmptyUserData()
  }

  const phoneNumberFromSdk = cleanPhoneNumber(nextUserData.phoneNumberFromSdk || nextUserData.phoneNumber || '')
  const phoneNumberWithUsernamePrefix = nextUserData.username
    ? nextUserData.username
    : buildPhoneNumberWithUsernamePrefix(phoneNumberFromSdk, usernamePrefix)

  return {
    ...createEmptyUserData(),
    ...nextUserData,
    phoneNumberFromSdk,
    phoneNumberWithUsernamePrefix
  }
}

const formatStoredUserData = (storedUserData, usernamePrefix) => {
  if (!storedUserData) {
    return createEmptyUserData()
  }

  const phoneNumberFromSdk = cleanPhoneNumber(storedUserData.phoneNumberFromSdk || '')
  const phoneNumberWithUsernamePrefix =
    storedUserData.phoneNumberWithUsernamePrefix || buildPhoneNumberWithUsernamePrefix(phoneNumberFromSdk, usernamePrefix)

  return {
    ...createEmptyUserData(),
    ...storedUserData,
    phoneNumberFromSdk,
    phoneNumberWithUsernamePrefix
  }
}

const hasCurrentUserData = (userData) => {
  if (userData?.sdkToken) {
    return true
  }
  return !!userData?.phoneNumberWithUsernamePrefix && validatePhoneNumber(userData?.phoneNumberFromSdk || '').isValid
}

const hasStoredUserData = (userData) => {
  return !!userData?.sdkToken || !!userData?.phoneNumberWithUsernamePrefix
}

const getStoredConfirmedUserData = (usernamePrefix) => {
  return formatStoredUserData(LocalStorageManager.getItem(APP_USER_CONFIRMED_STORAGE_KEY), usernamePrefix)
}

const saveConfirmedUserData = (userData) => {
  LocalStorageManager.setItem(APP_USER_CONFIRMED_STORAGE_KEY, {
    phoneNumberFromSdk: userData?.phoneNumberFromSdk || '',
    phoneNumberWithUsernamePrefix: userData?.phoneNumberWithUsernamePrefix || '',
    fullName: userData?.fullName || '',
    sdkToken: userData?.sdkToken || null
  })
}

export const AppUserContext = React.createContext(null)

export const AppUserContextProvider = ({ children }) => {
  const { usernamePrefix } = useAppParamsContext()
  const { getUserInfoBySdk } = useSdk()
  const [userData, setUserData] = React.useState(() => createEmptyUserData())
  const [pendingUserData, setPendingUserData] = React.useState(() => createEmptyUserData())
  const [identityStatus, setIdentityStatus] = React.useState(APP_USER_IDENTITY_STATUS.LOADING)
  const [isLoadingUserData, setIsLoadingUserData] = React.useState(true)
  const [sdkError, setSdkError] = React.useState(null)

  const refreshUserData = React.useCallback(
    async (payload) => {
      setIsLoadingUserData(true)

      try {
        const storedUserData = getStoredConfirmedUserData(usernamePrefix)
        const hasValidStoredUserData = hasStoredUserData(storedUserData)

        let sdkResult

        try {
          sdkResult = await getUserInfoBySdk(payload)
        } catch (error) {
          sdkResult = { status: SDK_RESULT_STATUS.NO_ATTEMPT }
        }

        // Failure: SDK đã thử nhưng thất bại → bắt buộc hiển thị màn lỗi, không fallback localStorage.
        if (sdkResult?.status === SDK_RESULT_STATUS.FAILURE) {
          setSdkError({
            errorCode: sdkResult.errorCode,
            source: sdkResult.source
          })
          setUserData(createEmptyUserData())
          setPendingUserData(createEmptyUserData())
          setIdentityStatus(APP_USER_IDENTITY_STATUS.SDK_AUTHENTICATION_ERROR)
          return undefined
        }

        setSdkError(null)

        // No attempt: không có cơ sở để gọi SDK → dùng lại data đã consent (nếu có).
        if (sdkResult?.status === SDK_RESULT_STATUS.NO_ATTEMPT) {
          if (hasValidStoredUserData) {
            setUserData(storedUserData)
            setPendingUserData(createEmptyUserData())
            setIdentityStatus(APP_USER_IDENTITY_STATUS.READY)
            return storedUserData
          }

          setUserData(createEmptyUserData())
          setPendingUserData(createEmptyUserData())
          setIdentityStatus(APP_USER_IDENTITY_STATUS.MISSING_PHONE)
          return undefined
        }

        // Success: có data mới từ SDK → đối chiếu với localStorage.
        const currentUserData = formatCurrentUserData(sdkResult?.data, usernamePrefix)
        const hasValidCurrentUserData = hasCurrentUserData(currentUserData)

        if (hasValidCurrentUserData) {
          if (!hasValidStoredUserData) {
            setUserData(createEmptyUserData())
            setPendingUserData(currentUserData)
            setIdentityStatus(APP_USER_IDENTITY_STATUS.CONSENT_FIRST_TIME)
            return currentUserData
          }

          if (currentUserData.phoneNumberWithUsernamePrefix === storedUserData.phoneNumberWithUsernamePrefix) {
            saveConfirmedUserData(currentUserData)
            setUserData(currentUserData)
            setPendingUserData(createEmptyUserData())
            setIdentityStatus(APP_USER_IDENTITY_STATUS.READY)
            return currentUserData
          }

          setUserData(createEmptyUserData())
          setPendingUserData(currentUserData)
          setIdentityStatus(APP_USER_IDENTITY_STATUS.CONSENT_CHANGED)
          return currentUserData
        }

        // SDK trả success nhưng data không hợp lệ (rất hiếm) → fallback theo cơ chế cũ.
        if (hasValidStoredUserData) {
          setUserData(storedUserData)
          setPendingUserData(createEmptyUserData())
          setIdentityStatus(APP_USER_IDENTITY_STATUS.USING_LOCAL_FALLBACK)
          return storedUserData
        }

        setUserData(createEmptyUserData())
        setPendingUserData(createEmptyUserData())
        setIdentityStatus(APP_USER_IDENTITY_STATUS.MISSING_PHONE)
        return undefined
      } finally {
        setIsLoadingUserData(false)
      }
    },
    [getUserInfoBySdk, usernamePrefix]
  )

  const confirmPendingUserData = React.useCallback(() => {
    if (!hasCurrentUserData(pendingUserData)) {
      return false
    }

    saveConfirmedUserData(pendingUserData)
    setUserData(pendingUserData)
    setPendingUserData(createEmptyUserData())
    setIdentityStatus(APP_USER_IDENTITY_STATUS.READY)

    return true
  }, [pendingUserData])

  const confirmManualPhoneUserData = React.useCallback(
  (phoneNumber) => {
    const manualUserData = formatCurrentUserData(
      {
        phoneNumberFromSdk: phoneNumber,
        phoneNumber
      },
      usernamePrefix
    )

    if (!hasCurrentUserData(manualUserData)) {
      return false
    }

    saveConfirmedUserData(manualUserData)
    setUserData(manualUserData)
    setPendingUserData(createEmptyUserData())
    setIdentityStatus(APP_USER_IDENTITY_STATUS.READY)

    return true
  },
  [usernamePrefix]
)

  const confirmSdkTokenData = React.useCallback(
    (sdkToken) => {
      const tokenData = {
        ...createEmptyUserData(),
        sdkToken: sdkToken
      }

      if (!hasCurrentUserData(tokenData)) {
        return false
      }

      saveConfirmedUserData(tokenData)
      setUserData(tokenData)
      setPendingUserData(createEmptyUserData())
      setIdentityStatus(APP_USER_IDENTITY_STATUS.READY)

      return true
    },
    []
  )
  React.useEffect(() => {
    void refreshUserData()
  }, [refreshUserData])

  const isConsentRequired = React.useMemo(() => {
    return [
      APP_USER_IDENTITY_STATUS.CONSENT_FIRST_TIME,
      APP_USER_IDENTITY_STATUS.CONSENT_CHANGED,
      APP_USER_IDENTITY_STATUS.MISSING_PHONE,
      APP_USER_IDENTITY_STATUS.SDK_AUTHENTICATION_ERROR
    ].includes(identityStatus)
  }, [identityStatus])

  const value = React.useMemo(
    () => ({
      userData,
      pendingUserData,
      identityStatus,
      isConsentRequired,
      isLoadingUserData,
      sdkError,
      refreshUserData,
      confirmPendingUserData,
      confirmManualPhoneUserData,
      confirmSdkTokenData
    }),
    [
      confirmManualPhoneUserData,
      confirmPendingUserData,
      identityStatus,
      isConsentRequired,
      isLoadingUserData,
      pendingUserData,
      refreshUserData,
      sdkError,
      userData,
      confirmSdkTokenData
    ]
  )

  return <AppUserContext.Provider value={value}>{children}</AppUserContext.Provider>
}

export const useAppUserContext = () => {
  const context = React.useContext(AppUserContext)

  if (!context) {
    throw new Error('useAppUserContext must be used within AppUserContextProvider')
  }

  return context
}
